/**
 * DecreeTemplatesTab — Ustawienia → Szablony dekretacji ("Podpowiedz dekret").
 *
 * Lista szablonów `decree_templates` PER FIRMA — mirror strukturalny `ChartOfAccountsTab`
 * (firma: top-bar w trybie jednej firmy używany wprost, tryb "Wszystkie firmy" → lokalny
 * picker firmy, patrz ten sam wzorzec tam). RÓŻNICA vs plan kont: JEDEN kod permission
 * (`documents.<klasa faktury>.manage_accounting`) dla odczytu I zapisu — projekt uprawnień
 * dedykowanych dekretacji jest odłożony (patrz `apps/api/app/api/decree-templates/route.ts`),
 * więc tu nie ma osobnego `.view`/`.manage` jak przy planie kont.
 *
 * CRUD: dodaj/edytuj/usuń (soft)/przywróć — `<ConfirmDialog>` (CLAUDE.md §4.12).
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Eye, EyeOff, Pencil, Plus, RotateCcw, Trash2 } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  type DecreeTemplateDocumentClass,
  DecreeTemplateFormModal,
  type DecreeTemplateFormValues,
  emptyDecreeTemplateFormValues,
} from '@/components/settings/DecreeTemplateFormModal';
import { Spinner } from '@/components/Spinner';
import { CompanyPicker } from '@/components/ui/CompanyPicker';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import {
  type DecreeTemplateServerLine,
  serverTemplateLineToDraft,
  templateDraftLineToPayload,
} from '@/lib/documents/decree-templates';
import { useActiveCompanyScope } from '@/lib/use-active-company';
import { useCompanies } from '@/lib/use-permissions';

// ─── Typy ───────────────────────────────────────────────────────────────────

interface DecreeTemplateRow {
  id: string;
  /** null = szablon „dla wszystkich firm" (wspólny). */
  companyId: string | null;
  documentClass: DecreeTemplateDocumentClass;
  documentTypeId: string | null;
  name: string;
  lines: DecreeTemplateServerLine[];
  isActive: boolean;
  gcrecord: string | null;
}

interface DecreeTemplatesListResponse {
  templates: DecreeTemplateRow[];
}

// Dekretacja obejmuje OBIE klasy faktur, a te mają od F24 osobne kody uprawnień —
// wystarczy prawo po jednej ze stron (mirror `lib/documents/accounting-permission` w API).
const MANAGE_PERMS = [
  'documents.purchase_invoice.manage_accounting',
  'documents.sale_invoice.manage_accounting',
] as const;

// ─── Helpers ────────────────────────────────────────────────────────────────

function formToValues(row: DecreeTemplateRow): DecreeTemplateFormValues {
  return {
    name: row.name,
    documentClass: row.documentClass,
    documentTypeId: row.documentTypeId ?? '',
    isActive: row.isActive,
    forAllCompanies: row.companyId === null,
    lines: row.lines.map(serverTemplateLineToDraft),
  };
}

// ─── Główny komponent ────────────────────────────────────────────────────────

export function DecreeTemplatesTab() {
  const { t } = useTranslation();
  const { me } = useTenantAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const canManage =
    me?.user.isSuperUser === true ||
    MANAGE_PERMS.some((code) => me?.permissions.includes(code) ?? false);

  const companyScope = useActiveCompanyScope();
  const companiesQ = useCompanies();
  const companies = useMemo(() => companiesQ.data?.companies ?? [], [companiesQ.data]);

  const [manualCompanyId, setManualCompanyId] = useState<string | null>(null);
  const effectiveCompanyId =
    companyScope.mode === 'company' ? companyScope.companyId : manualCompanyId;

  const [showDeleted, setShowDeleted] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<DecreeTemplateRow | null>(null);
  const [pendingDelete, setPendingDelete] = useState<DecreeTemplateRow | null>(null);
  const [pendingRestore, setPendingRestore] = useState<DecreeTemplateRow | null>(null);

  const queryKey = ['decree-templates', 'list', effectiveCompanyId, showDeleted] as const;

  const listQ = useQuery({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (effectiveCompanyId) params.set('companyId', effectiveCompanyId);
      if (showDeleted) params.set('include_deleted', 'true');
      const res = await api.get<DecreeTemplatesListResponse>(
        `/decree-templates?${params.toString()}`,
      );
      return res.data;
    },
    enabled: !!effectiveCompanyId,
    retry: false,
  });

  const templates = listQ.data?.templates ?? [];

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['decree-templates', 'list', effectiveCompanyId] });
    // Picker "Podpowiedz dekret" na stronach faktur (queryKey ['decree-templates', 'picker', ...]).
    queryClient.invalidateQueries({ queryKey: ['decree-templates', 'picker'] });
  }

  function errorCode(err: unknown): string | null {
    return isAxiosError(err)
      ? ((err.response?.data as { error?: string } | undefined)?.error ?? null)
      : null;
  }

  function mapErrorMessage(code: string | null): string {
    switch (code) {
      case 'TEMPLATE_NAME_TAKEN':
        return t('tenant.documents.decree.templates.toast.nameTaken');
      case 'COMPANY_NOT_FOUND':
        return t('tenant.documents.decree.templates.toast.companyNotFound');
      case 'TEMPLATE_NOT_FOUND':
        return t('tenant.documents.decree.templates.toast.notFound');
      case 'NOT_DELETED':
        return t('tenant.documents.decree.templates.toast.notDeleted');
      default:
        return t('tenant.documents.decree.templates.toast.genericError');
    }
  }

  const createMutation = useMutation({
    mutationFn: async (values: DecreeTemplateFormValues) => {
      // companyId null = szablon „dla wszystkich firm"; inaczej firma wybrana w ekranie.
      if (!values.forAllCompanies && !effectiveCompanyId) throw new Error('NO_COMPANY');
      const body = {
        companyId: values.forAllCompanies ? null : effectiveCompanyId,
        documentClass: values.documentClass,
        documentTypeId: values.documentTypeId || null,
        name: values.name,
        lines: values.lines.map(templateDraftLineToPayload),
        isActive: values.isActive,
      };
      const res = await api.post<{ template: DecreeTemplateRow }>('/decree-templates', body);
      return res.data.template;
    },
    onSuccess: () => {
      invalidate();
      toast.show({
        variant: 'success',
        title: t('tenant.documents.decree.templates.toast.created'),
      });
      setCreateOpen(false);
    },
    onError: (err) => {
      toast.show({ variant: 'error', title: mapErrorMessage(errorCode(err)) });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, values }: { id: string; values: DecreeTemplateFormValues }) => {
      const patch = {
        name: values.name,
        documentTypeId: values.documentTypeId || null,
        lines: values.lines.map(templateDraftLineToPayload),
        isActive: values.isActive,
      };
      const res = await api.patch<{ template: DecreeTemplateRow }>(
        `/decree-templates/${id}`,
        patch,
      );
      return res.data.template;
    },
    onSuccess: () => {
      invalidate();
      toast.show({
        variant: 'success',
        title: t('tenant.documents.decree.templates.toast.updated'),
      });
      setEditing(null);
    },
    onError: (err) => {
      toast.show({ variant: 'error', title: mapErrorMessage(errorCode(err)) });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/decree-templates/${id}`);
    },
    onSuccess: () => {
      invalidate();
      toast.show({
        variant: 'success',
        title: t('tenant.documents.decree.templates.toast.deleted'),
      });
      setPendingDelete(null);
    },
    onError: (err) => {
      toast.show({ variant: 'error', title: mapErrorMessage(errorCode(err)) });
      setPendingDelete(null);
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/decree-templates/${id}/restore`);
    },
    onSuccess: () => {
      invalidate();
      toast.show({
        variant: 'success',
        title: t('tenant.documents.decree.templates.toast.restored'),
      });
      setPendingRestore(null);
    },
    onError: (err) => {
      toast.show({ variant: 'error', title: mapErrorMessage(errorCode(err)) });
      setPendingRestore(null);
    },
  });

  const isModuleInactive = isAxiosError(listQ.error) && listQ.error.response?.status === 404;

  return (
    <div data-testid="decree-templates-tab">
      <header className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">
            {t('tenant.documents.decree.templates.title')}
          </h2>
          <p className="text-xs text-text-tertiary">
            {t('tenant.documents.decree.templates.subtitle')}
          </p>
        </div>
        {canManage && effectiveCompanyId && (
          <Button
            variant="primary"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => setCreateOpen(true)}
            data-testid="decree-template-new-btn"
          >
            {t('tenant.documents.decree.templates.newTemplate')}
          </Button>
        )}
      </header>

      {companyScope.mode !== 'company' && (
        <div className="mb-4 max-w-xs">
          <label className="mb-1 block text-xs font-medium text-text-secondary">
            {t('tenant.documents.decree.templates.companyPickerLabel')}
          </label>
          <CompanyPicker
            value={manualCompanyId}
            onChange={setManualCompanyId}
            companies={companies.map((c) => ({ id: c.id, legalName: c.legalName }))}
            required
            testId="decree-templates-company-picker"
          />
        </div>
      )}

      {canManage && effectiveCompanyId && (
        <div className="mb-3 flex justify-end">
          <button
            type="button"
            onClick={() => setShowDeleted((v) => !v)}
            data-testid="decree-templates-show-deleted"
            className={
              'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition ' +
              (showDeleted
                ? 'border-accent-500 bg-accent-500/10 text-accent-500'
                : 'border-border-primary bg-bg-primary text-text-secondary hover:border-accent-500 hover:text-accent-500')
            }
          >
            {showDeleted ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {t('tenant.documents.decree.templates.showDeleted')}
          </button>
        </div>
      )}

      {!effectiveCompanyId ? (
        <Card padding="md" data-testid="decree-templates-no-company">
          <p className="text-sm text-text-tertiary">
            {t('tenant.documents.decree.templates.selectCompanyPrompt')}
          </p>
        </Card>
      ) : listQ.isLoading ? (
        <div className="flex items-center justify-center py-10">
          <Spinner />
        </div>
      ) : isModuleInactive ? (
        <Card padding="md">
          <p className="text-sm text-text-tertiary">
            {t('tenant.documents.decree.templates.noPermission')}
          </p>
        </Card>
      ) : listQ.isError ? (
        <Card padding="md">
          <p className="text-sm text-danger-600">{mapErrorMessage(errorCode(listQ.error))}</p>
        </Card>
      ) : templates.length === 0 ? (
        <Card padding="md">
          <p className="text-sm text-text-tertiary">
            {t('tenant.documents.decree.templates.empty')}
          </p>
        </Card>
      ) : (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="decree-templates-list">
              <thead className="border-b border-border-primary bg-bg-secondary text-xs uppercase tracking-wider text-text-muted">
                <tr>
                  <th className="px-3 py-2 text-left">
                    {t('tenant.documents.decree.templates.list.colName')}
                  </th>
                  <th className="px-3 py-2 text-left">
                    {t('tenant.documents.decree.templates.list.colClass')}
                  </th>
                  <th className="px-3 py-2 text-left">
                    {t('tenant.documents.decree.templates.list.colLines')}
                  </th>
                  <th className="px-3 py-2 text-left">
                    {t('tenant.documents.decree.templates.list.colStatus')}
                  </th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border-primary">
                {templates.map((row) => {
                  const isDeleted = row.gcrecord !== null;
                  return (
                    <tr
                      key={row.id}
                      data-testid={`decree-template-row-${row.id}`}
                      className={isDeleted || !row.isActive ? 'opacity-50' : undefined}
                    >
                      <td className="px-3 py-2 font-medium text-text-primary">{row.name}</td>
                      <td className="px-3 py-2">
                        <Badge color="accent">
                          {t(
                            `tenant.documents.decree.templates.documentClasses.${row.documentClass}`,
                          )}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-text-secondary">{row.lines.length}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap items-center gap-1">
                          {row.companyId === null && (
                            <Badge color="blue">
                              {t('tenant.documents.decree.templates.badges.allCompanies')}
                            </Badge>
                          )}
                          {!row.isActive && (
                            <Badge color="gray">
                              {t('tenant.documents.decree.templates.badges.inactive')}
                            </Badge>
                          )}
                          {isDeleted && (
                            <Badge color="red">
                              {t('tenant.documents.decree.templates.badges.deleted')}
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        {canManage && (
                          <div className="flex items-center justify-end gap-1">
                            {isDeleted ? (
                              <button
                                type="button"
                                onClick={() => setPendingRestore(row)}
                                aria-label={t('common.restore')}
                                data-testid={`decree-template-restore-${row.id}`}
                                className="rounded p-1 text-text-muted hover:bg-bg-tertiary hover:text-accent-600"
                              >
                                <RotateCcw className="h-3.5 w-3.5" />
                              </button>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  onClick={() => setEditing(row)}
                                  aria-label={t('common.edit')}
                                  data-testid={`decree-template-edit-${row.id}`}
                                  className="rounded p-1 text-text-muted hover:bg-bg-tertiary hover:text-accent-600"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setPendingDelete(row)}
                                  aria-label={t('common.delete')}
                                  data-testid={`decree-template-delete-${row.id}`}
                                  className="rounded p-1 text-text-muted hover:bg-bg-tertiary hover:text-danger-600"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <DecreeTemplateFormModal
        open={createOpen}
        mode="create"
        initialValues={emptyDecreeTemplateFormValues()}
        companyId={effectiveCompanyId}
        allowAllCompanies={me?.user.isSuperUser === true}
        saving={createMutation.isPending}
        onSubmit={(values) => createMutation.mutate(values)}
        onClose={() => setCreateOpen(false)}
      />

      <DecreeTemplateFormModal
        open={editing !== null}
        mode="edit"
        initialValues={editing ? formToValues(editing) : emptyDecreeTemplateFormValues()}
        companyId={effectiveCompanyId}
        allowAllCompanies={me?.user.isSuperUser === true}
        saving={updateMutation.isPending}
        onSubmit={(values) => {
          if (editing) updateMutation.mutate({ id: editing.id, values });
        }}
        onClose={() => setEditing(null)}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        variant="danger"
        title={t('tenant.documents.decree.templates.confirmDelete.title')}
        message={
          pendingDelete
            ? t('tenant.documents.decree.templates.confirmDelete.message', {
                name: pendingDelete.name,
              })
            : ''
        }
        confirming={deleteMutation.isPending}
        onConfirm={() => pendingDelete && deleteMutation.mutate(pendingDelete.id)}
        onClose={() => setPendingDelete(null)}
      />

      <ConfirmDialog
        open={pendingRestore !== null}
        variant="primary"
        title={t('tenant.documents.decree.templates.confirmRestore.title')}
        message={
          pendingRestore
            ? t('tenant.documents.decree.templates.confirmRestore.message', {
                name: pendingRestore.name,
              })
            : ''
        }
        confirmLabel={t('common.restore')}
        confirming={restoreMutation.isPending}
        onConfirm={() => pendingRestore && restoreMutation.mutate(pendingRestore.id)}
        onClose={() => setPendingRestore(null)}
      />
    </div>
  );
}
